export interface OrangeSenseiIconProps {
  /**
   * Icon variant/expression
   * - smile: 通常の笑顔
   * - happy: とても嬉しい表情
   * - listening: 聞いている表情
   * - thinking: 考えている表情
   * - surprising: 驚いている表情
   */
  variant?: "smile" | "happy" | "listening" | "thinking" | "surprising";
  /** Size of the icon in pixels */
  size?: number;
}

export function OrangeSenseiIcon({
  variant = "smile",
  size = 32,
}: OrangeSenseiIconProps) {
  const orangeColor = "#F15025";
  const creamColor = "#F5F4ED";

  const renderIcon = () => {
    switch (variant) {
      case "smile":
        return (
          <svg
            width={size}
            height={size}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Outer circle (border) */}
            <circle
              cx="16"
              cy="16"
              r="14.4"
              stroke={orangeColor}
              strokeWidth="1.8"
              fill="none"
            />
            {/* Inner circle (fill) */}
            <circle cx="16" cy="16" r="12" fill={orangeColor} />
            {/* Smile expression */}
            <g>
              <path
                d="M9.86 16.13C9.86 14.54 11.05 13.75 12.44 13.75C13.83 13.75 14.82 14.94 14.82 16.33"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M22.56 16.13C22.56 14.54 21.37 13.75 19.98 13.75C18.59 13.75 17.6 14.94 17.6 16.33"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M10.06 11.17C11.39 10.58 12.23 10.42 14.02 10.57"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M22.35 11.17C21.03 10.58 20.18 10.42 18.39 10.57"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </g>
          </svg>
        );

      case "happy":
        return (
          <svg
            width={size}
            height={size}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Outer circle (border) */}
            <circle
              cx="16"
              cy="16"
              r="14.4"
              stroke={orangeColor}
              strokeWidth="1.8"
              fill="none"
            />
            {/* Inner circle (fill) */}
            <circle cx="16" cy="16" r="12" fill={orangeColor} />
            {/* Happy expression */}
            <g>
              {/* Left eye curve */}
              <path
                d="M9.86 16.13C9.86 14.54 11.05 13.75 12.44 13.75C13.83 13.75 14.82 14.94 14.82 16.33"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Right eye curve */}
              <path
                d="M22.56 16.13C22.56 14.54 21.37 13.75 19.98 13.75C18.59 13.75 17.6 14.94 17.6 16.33"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Left eyebrow */}
              <path
                d="M10.06 11.17C11.39 10.58 12.23 10.42 14.02 10.57"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Right eyebrow */}
              <path
                d="M22.35 11.17C21.03 10.58 20.18 10.42 18.39 10.57"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </g>
          </svg>
        );

      case "listening":
        return (
          <svg
            width={size}
            height={size}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Outer circle (border) */}
            <circle
              cx="16"
              cy="16"
              r="14.4"
              stroke={orangeColor}
              strokeWidth="1.8"
              fill="none"
            />
            {/* Inner circle (fill) */}
            <circle cx="16" cy="16" r="12" fill={orangeColor} />
            {/* Listening expression */}
            <g>
              {/* Left eye curve (upside down) */}
              <path
                d="M10.86 16.69C10.86 18.28 12.05 19.07 13.44 19.07C14.83 19.07 15.82 17.88 15.82 16.49"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Right eye curve (upside down) */}
              <path
                d="M22.76 16.69C22.76 18.28 21.57 19.07 20.18 19.07C18.79 19.07 17.8 17.88 17.8 16.49"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Left eyebrow */}
              <path
                d="M11.66 11.25C13.07 10.89 13.92 10.88 15.67 11.32"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Right eyebrow */}
              <path
                d="M22.25 11.25C20.84 10.89 19.98 10.88 18.23 11.32"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </g>
          </svg>
        );

      case "thinking":
        return (
          <svg
            width={size}
            height={size}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Outer circle (border) */}
            <circle
              cx="16"
              cy="16"
              r="14.4"
              stroke={orangeColor}
              strokeWidth="1.8"
              fill="none"
            />
            {/* Inner circle (fill) */}
            <circle cx="16" cy="16" r="12" fill={orangeColor} />
            {/* Thinking expression */}
            <g>
              {/* Left eye curve */}
              <path
                d="M10.86 16.68C10.86 18.27 12.05 19.06 13.44 19.06C14.83 19.06 15.82 17.87 15.82 16.48"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Right eye curve */}
              <path
                d="M22.56 16.68C22.56 18.27 21.37 19.06 19.98 19.06C18.59 19.06 17.6 17.87 17.6 16.48"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Left eyebrow */}
              <path
                d="M12.6 11.53C13.56 10.88 14.08 10.9 15.24 11.15"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Right eyebrow */}
              <path
                d="M18.8 12.45C20.17 12.93 21.03 13.01 22.8 12.72"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </g>
          </svg>
        );

      case "surprising":
        return (
          <svg
            width={size}
            height={size}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Outer circle (border) */}
            <circle
              cx="16"
              cy="16"
              r="14.4"
              stroke={orangeColor}
              strokeWidth="1.8"
              fill="none"
            />
            {/* Inner circle (fill) */}
            <circle cx="16" cy="16" r="12" fill={orangeColor} />
            {/* Surprising expression */}
            <g>
              {/* Left eyebrow */}
              <path
                d="M9.86 14.06C10.65 12.84 11.28 12.25 12.86 11.39"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Right eyebrow */}
              <path
                d="M25.16 14.06C24.37 12.84 23.75 12.25 22.16 11.39"
                stroke={creamColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Left eye circle */}
              <circle
                cx="13.7"
                cy="18.26"
                r="2.78"
                stroke={creamColor}
                strokeWidth="1.5"
              />
              {/* Right eye circle */}
              <circle
                cx="21.44"
                cy="18.26"
                r="2.78"
                stroke={creamColor}
                strokeWidth="1.5"
              />
            </g>
          </svg>
        );

      default:
        return null;
    }
  };

  return <div style={{ width: size, height: size }}>{renderIcon()}</div>;
}
